import { useState } from "react";
import { login, signup, resetRequest, resetPassword } from "../authApi";

type Mode = "signin" | "signup" | "forgot" | "reset";

// Full-screen owner login. Gates the private studio (all contracts + ledgers).
// First-time visitors create their password; a reset link (?reset=token) opens
// straight into the reset form. Clients never see this — they use share links.
export function AuthGate({ resetToken, onDone }: { resetToken?: string | null; onDone: (email: string) => void }) {
  const [mode, setMode] = useState<Mode>(resetToken ? "reset" : "signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function submit() {
    if (busy) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      if (mode === "signin") onDone((await login(email.trim(), password)).email);
      else if (mode === "signup") onDone((await signup(email.trim(), password)).email);
      else if (mode === "reset") onDone((await resetPassword(resetToken!, password)).email);
      else if (mode === "forgot") {
        const r = await resetRequest(email.trim());
        setMsg(r.emailConfigured
          ? "If that email has an account, a reset link is on its way — check your inbox (and spam)."
          : "Email delivery isn’t connected yet, so a reset link can’t be sent. Set the RESEND_API_KEY, or sign in with your password.");
      }
    } catch (e: any) {
      setErr(String(e?.message || e).slice(0, 160));
    } finally { setBusy(false); }
  }

  const title = mode === "signup" ? "Create your studio" : mode === "forgot" ? "Reset your password" : mode === "reset" ? "Set a new password" : "Your studio";
  const sub =
    mode === "signup" ? "Set the password that protects all your contracts, ledgers and certificates. Only you get in here." :
    mode === "forgot" ? "Enter your account email and we’ll send you a link to set a new password." :
    mode === "reset" ? "Choose a new password for your studio." :
    "Sign in to your private studio — all your contracts, ledgers and payment certificates in one place.";
  const cta = mode === "signup" ? "Create studio →" : mode === "forgot" ? "Send reset link →" : mode === "reset" ? "Save password →" : "Sign in →";
  const canSubmit = mode === "forgot" ? !!email.trim() : mode === "reset" ? password.length >= 8 : !!email.trim() && !!password;

  return (
    <div className="landing landing--ready authgate">
      <div className="ld-main">
        <header className="ld-head">
          <div className="ld-brand">Studio<span className="ld-dot2"> · </span>Contract</div>
        </header>
        <div className="ag-wrap">
          <div className="ag-card">
            <div className="gate-kicker">{mode === "signup" ? "First time" : "Private access"}</div>
            <h1 className="ag-title">{title}</h1>
            <p className="ag-sub">{sub}</p>

            {mode !== "reset" && (
              <label className="ag-field"><span className="lbl">Email</span>
                <input type="email" autoFocus value={email} placeholder="you@studiogy.design"
                  onChange={(e) => { setEmail(e.target.value); setErr(null); }} onKeyDown={(e) => e.key === "Enter" && submit()} />
              </label>
            )}
            {mode !== "forgot" && (
              <label className="ag-field"><span className="lbl">Password {(mode === "signup" || mode === "reset") && <span className="muted">(min 8 characters)</span>}</span>
                <input type="password" autoFocus={mode === "reset"} value={password} placeholder="••••••••"
                  onChange={(e) => { setPassword(e.target.value); setErr(null); }} onKeyDown={(e) => e.key === "Enter" && submit()} />
              </label>
            )}

            {err && <div className="ag-err">{err}</div>}
            {msg && <div className="ag-msg">{msg}</div>}

            <button className="ld-btn-dark ag-cta" onClick={submit} disabled={busy || !canSubmit}>{busy ? <span className="spinner" /> : cta}</button>

            <div className="ag-links">
              {mode === "signin" && <>
                <button className="link" onClick={() => { setErr(null); setMsg(null); setMode("signup"); }}>First time? Create your studio</button>
                <button className="link" onClick={() => { setErr(null); setMsg(null); setMode("forgot"); }}>Forgot password?</button>
              </>}
              {mode === "signup" && <button className="link" onClick={() => { setErr(null); setMsg(null); setMode("signin"); }}>Already set up? Sign in</button>}
              {(mode === "forgot" || mode === "reset") && <button className="link" onClick={() => { setErr(null); setMsg(null); setMode("signin"); }}>← Back to sign in</button>}
            </div>
          </div>
          <p className="ag-foot">Reviewers and clients don’t sign in here — they open the review or tracking link you share with them.</p>
        </div>
      </div>
    </div>
  );
}
